/**
 * ActionWizard — Unified Event/Task Creation Wizard
 * Types for the wizard component system
 */

import type { EventTemplateCategory, EventTemplate } from '../../types/eventTemplates';

/** Target entity for the action */
export interface ActionTarget {
  plantingId?: string;  // UUID
  bedId?: string;       // UUID
  plotId?: string;      // UUID
  fieldId?: string;     // UUID
  // Optional display info
  label?: string;       // e.g. "Ель колючая 'Hoopsii'"
  cropId?: number;
  cropName?: string;
  bedLabel?: string;    // e.g. "Ряд 3, Поз 5"
}

/** Action mode — what user intends to do */
export type ActionMode = 'immediate' | 'assign';

/** Wizard step */
export type WizardStep = 'select-action' | 'fill-data';

/** Supercategory grouping */
export type ActionSupercategory = 'observation' | 'action';

/** Supercategory definition */
export interface SupercategoryDef {
  key: ActionSupercategory;
  label: string;
  icon: string;
  color: string;
  borderColor: string;
  categories: EventTemplateCategory[];
}

/** Supercategories config */
export const SUPERCATEGORIES: SupercategoryDef[] = [
  {
    key: 'observation',
    label: 'Наблюдения',
    icon: '📊',
    color: '#e6f7ff',
    borderColor: '#91d5ff',
    categories: ['inspection', 'measurement'],
  },
  {
    key: 'action',
    label: 'Уход и обработка',
    icon: '🔧',
    color: '#fff7e6',
    borderColor: '#ffd591',
    categories: ['treatment', 'care', 'maintenance', 'harvest'],
  },
];

/** Category display names */
export const CATEGORY_LABELS: Record<string, string> = {
  inspection: 'Осмотры',
  measurement: 'Измерения',
  treatment: 'Обработки',
  care: 'Уход',
  maintenance: 'Обслуживание',
  harvest: 'Урожай',
  lifecycle: 'Жизненный цикл',
};

/** Get supercategory for a given template category */
export function getSupercategory(category: EventTemplateCategory): ActionSupercategory | null {
  for (const sc of SUPERCATEGORIES) {
    if ((sc.categories as string[]).includes(category)) {
      return sc.key;
    }
  }
  return null;
}

/** Group templates by supercategory */
export function groupTemplatesBySupercategory(
  templates: EventTemplate[]
): Map<ActionSupercategory, EventTemplate[]> {
  const result = new Map<ActionSupercategory, EventTemplate[]>();
  for (const sc of SUPERCATEGORIES) {
    result.set(sc.key, []);
  }
  for (const t of templates) {
    const sc = getSupercategory(t.category);
    if (sc) {
      result.get(sc)!.push(t);
    }
  }
  return result;
}

/** Props for ActionWizard */
export interface ActionWizardProps {
  /** Target entities for the action */
  targets: ActionTarget[];
  /** Farm UUID */
  farmId: string;
  /** Optional crop ID for property binding filtering */
  cropId?: number | null;
  /** Planting type (for batch plantings) */
  plantingType?: 'single' | 'batch';
  /** Planting quantity (for batch plantings) */
  plantingQuantity?: number;
  /** Modal open state */
  open: boolean;
  /** Close callback */
  onClose: () => void;
  /** Success callback (refetch data) */
  onSuccess?: () => void | Promise<void>;
  /** Optional callback: start transplant target selection directly on plan grid */
  onStartTransplantSelection?: (params: {
    targets: ActionTarget[];
    sourceBedId: string;
    sourcePlotId: string;
  }) => void;
  /** Context label for display (e.g. "Ель колючая 'Hoopsii'" or "5 грядок на участке Теплица") */
  contextLabel?: string;
  /** When set, wizard operates in simulation mode — events are created as sim events */
  simSessionId?: string;
  /** Save id of the active simulation save. Required for simulation-mode events (CN-0030 phase 13). */
  simSaveId?: string;
  /** Current simulation date (ISO string, e.g. '2026-06-15'). Used as default event date in sim mode. */
  simDisplayDate?: string;
}
